import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/apiClient";
import { Plus, Sparkles, FileText, ArrowRight, Clock, CheckCircle2, Store, Loader2, AlertTriangle } from "lucide-react";
import { useT } from "../lib/i18n";
import { AGENTS } from "../data/agents";
import AgentIcon from "../components/AgentIcon";

const StatusBadge = ({ status }) => {
  const { t } = useT();
  const map = {
    completed: { c: "bg-[#10B981]/10 text-[#10B981]", i: CheckCircle2 },
    draft: { c: "bg-[#0EA5E9]/10 text-[#0EA5E9]", i: Clock },
    running: { c: "bg-[#F59E0B]/10 text-[#F59E0B]", i: Loader2 },
    failed: { c: "bg-[#EF4444]/10 text-[#EF4444]", i: AlertTriangle },
  };
  const cfg = map[status] || map.draft;
  const I = cfg.i;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold font-mono-kz uppercase tracking-wider ${cfg.c}`}><I className={`w-3 h-3 ${status === "running" ? "animate-spin" : ""}`} /> {t(status)}</span>;
};

export default function Dashboard() {
  const { t } = useT();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/sessions");
        setSessions(r.data);
      } catch (e) {
        // list stays empty; user can retry via navigation
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <DashboardLayout
      title={t("Decision command center")}
      action={
        <Link to="/sessions/new" data-testid="dashboard-new-session-btn" className="inline-flex items-center gap-2 bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-5 py-2.5 rounded-lg font-bold text-sm font-display">
          <Plus className="w-4 h-4" /> {t("New session")}
        </Link>
      }
    >
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display font-extrabold text-[#0B1B3D] text-lg">{t("Recent sessions")}</h2>
            <span className="font-mono-kz text-xs text-[#94A3B8] tracking-widest">{sessions.length} TOTAL</span>
          </div>
          {loading && <div className="text-sm text-[#94A3B8]">Loading…</div>}
          {!loading && sessions.length === 0 && (
            <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
              <Sparkles className="w-8 h-8 mx-auto text-[#0EA5E9]" />
              <p className="mt-3 font-display font-bold text-[#0B1B3D]">{t("No sessions yet")}</p>
              <p className="text-sm text-[#475569] mt-1">{t("Start your first AI expert panel in under a minute.")}</p>
              <Link to="/sessions/new" data-testid="empty-state-cta" className="inline-flex mt-5 items-center gap-2 bg-[#0B1B3D] text-white px-5 py-2.5 rounded-lg font-bold text-sm font-display hover:bg-[#1E3A8A]">
                <Plus className="w-4 h-4" /> {t("Create decision session")}
              </Link>
            </div>
          )}
          {!loading && sessions.length > 0 && (
            <div className="space-y-3" data-testid="sessions-list">
              {sessions.map((s) => (
                <Link key={s.session_id} to={s.status === "completed" ? `/sessions/${s.session_id}/report` : `/sessions/${s.session_id}/workspace`} data-testid={`session-row-${s.session_id}`} className="block border border-slate-200 rounded-xl p-4 hover:border-[#0EA5E9] hover:shadow-sm transition-all">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-display font-extrabold text-[#0B1B3D] truncate">{s.title}</p>
                      <p className="text-sm text-[#475569] truncate mt-0.5">{s.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <StatusBadge status={s.status} />
                        <span className="text-xs text-[#94A3B8] font-mono-kz">{s.agent_ids.length} {t("agents")} · {s.rounds} {t("rounds")}</span>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-[#94A3B8]" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-display font-extrabold text-[#0B1B3D] text-lg">{t("Recommended panels")}</h3>
            <p className="text-sm text-[#475569] mt-1">{t("Quick-start templates for common decisions.")}</p>
            <div className="mt-4 space-y-2">
              {[
                { t: "VAT optimization", a: ["tax", "cfo"] },
                { t: "Pricing strategy", a: ["pricing", "sales", "cfo"] },
                { t: "Vendor selection", a: ["procurement", "legal", "operations"] },
              ].map((p) => (
                <Link key={p.t} to={`/sessions/new?template=${encodeURIComponent(p.t)}&agents=${p.a.join(",")}`} data-testid={`recommended-${p.t.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors">
                  <span className="font-bold text-[#0B1B3D] text-sm">{t(p.t)}</span>
                  <div className="flex -space-x-2">
                    {p.a.map((id) => {
                      const a = AGENTS.find((x) => x.id === id);
                      return a ? (
                        <div key={id} className="w-7 h-7 rounded-full bg-[#0B1B3D] border-2 border-white grid place-items-center text-white" title={a.name}>
                          <AgentIcon name={a.icon} className="w-3.5 h-3.5" />
                        </div>
                      ) : null;
                    })}
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-[#0B1B3D] text-white rounded-xl p-6">
            <Store className="w-6 h-6 text-[#10B981]" />
            <h3 className="mt-4 font-display font-extrabold text-lg">{t("Marketplace preview")}</h3>
            <p className="text-sm text-slate-300 mt-1">{t("Industry packs and human expert reviews — coming soon.")}</p>
            <Link to="/marketplace" data-testid="marketplace-preview-link" className="inline-flex mt-4 text-sm font-bold text-[#10B981] items-center gap-1 hover:underline">{t("Browse marketplace")} <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <FileText className="w-6 h-6 text-[#0EA5E9]" />
            <h3 className="mt-4 font-display font-extrabold text-[#0B1B3D] text-lg">{t("Saved reports")}</h3>
            <p className="text-sm text-[#475569] mt-1">{t("All completed decision reports live in your sessions list and can be exported as PDF.")}</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
